import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { apiService } from "../../services/chatApi";

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  documentName: string;
  documentTitle: string;
}

interface CharacterCount {
  current: number;
  max: number;
  isValid: boolean;
}

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
  open,
  onClose,
  documentName,
  documentTitle,
}) => {
  // ========================================
  // STATE MANAGEMENT
  // ========================================

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Character count tracking
  const titleCount: CharacterCount = {
    current: title.length,
    max: 100,
    isValid: title.length > 0 && title.length <= 100,
  };

  const descriptionCount: CharacterCount = {
    current: description.length,
    max: 400,
    isValid: description.length > 0 && description.length <= 400,
  };

  // Form validation
  const isFormValid = titleCount.isValid && descriptionCount.isValid;

  // ========================================
  // EVENT HANDLERS
  // ========================================

  const handleClose = () => {
    // Reset form state
    setTitle("");
    setDescription("");
    setError(null);
    setSuccess(false);
    setLoading(false);
    onClose();
  };

  const handleSubmit = async () => {
    // Client-side validation
    if (!isFormValid) {
      setError("Bitte füllen Sie alle Felder korrekt aus.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // API call via chatApi service
      await apiService.submitTicket({
        title: title.trim(),
        description: description.trim(),
        document_name: documentName,
        document_title: documentTitle,
      });

      // Success handling
      setSuccess(true);
      setLoading(false);

      // Auto-close after 2 seconds on success
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err: any) {
      setLoading(false);

      // Error handling with user-friendly messages
      if (err.response) {
        const status = err.response.status;
        const message = err.response.data?.error || err.message;

        if (status === 400) {
          setError(message || "Ungültige Eingabe. Bitte überprüfen Sie Ihre Angaben.");
        } else if (status === 500) {
          setError("Serverfehler. Bitte versuchen Sie es später erneut oder kontaktieren Sie den Administrator.");
        } else {
          setError(message || "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
        }
      } else if (err.request) {
        setError("Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.");
      } else {
        setError(err.message || "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
      }
    }
  };

  // ========================================
  // RENDER
  // ========================================

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Problem melden</DialogTitle>

      <DialogContent>
        {/* Document Info */}
        <Box sx={{ mb: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
          <Typography variant="body2" color="textSecondary">
            <strong>Dokument:</strong> {documentName}
          </Typography>
          {documentTitle && (
            <Typography variant="body2" color="textSecondary">
              <strong>Titel:</strong> {documentTitle}
            </Typography>
          )}
        </Box>

        {/* Success Message */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Ticket erfolgreich gesendet! Vielen Dank für Ihr Feedback.
          </Alert>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Title Field */}
        <TextField
          autoFocus
          margin="dense"
          label="Titel"
          type="text"
          fullWidth
          variant="outlined"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={loading || success}
          error={title.length > 0 && !titleCount.isValid}
          helperText={
            <Box
              component="span"
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              <span>Kurze Zusammenfassung des Problems</span>
              <span
                style={{
                  color: titleCount.isValid ? "inherit" : "red",
                }}
              >
                {titleCount.current}/{titleCount.max}
              </span>
            </Box>
          }
          inputProps={{
            maxLength: 100,
          }}
          sx={{ mb: 2 }}
        />

        {/* Description Field */}
        <TextField
          margin="dense"
          label="Beschreibung"
          type="text"
          fullWidth
          multiline
          rows={5}
          variant="outlined"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={loading || success}
          error={description.length > 0 && !descriptionCount.isValid}
          helperText={
            <Box
              component="span"
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              <span>
                Detaillierte Beschreibung (Rechtschreibfehler, Formatierung,
                Inhaltsfehler, etc.)
              </span>
              <span
                style={{
                  color: descriptionCount.isValid ? "inherit" : "red",
                }}
              >
                {descriptionCount.current}/{descriptionCount.max}
              </span>
            </Box>
          }
          inputProps={{
            maxLength: 400,
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || loading || success}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Wird gesendet...
            </>
          ) : success ? (
            "✓ Gesendet"
          ) : (
            "Senden"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDialog;
